import { useTranslation } from 'react-i18next';
import React, { useContext, useState } from 'react';
import NoSharedFleets from '../../../../../assets/images/ill_empty_access_sharing_fleets.png';
import { Button } from '@progress/kendo-react-buttons';
import { AcessSharingFilters } from '../..';
import SharedFleetsRow from './SharedFleetsRow';
import { Input } from '@progress/kendo-react-inputs';
import SearchIcon from 'assets/images/ic_search.svg';
import { ic_close_small_black as BlackCloseIcon } from 'app/widgets/SVGs';
import { useNavigate } from 'react-router-dom';
import {
  EditFleetDialog,
  EditFleetNameDialog,
} from 'app/components/access-sharing/dialogs';
import useTryMutate from 'app/hooks/useTryMutate';
import { ShareAccessEquipmentGuideProvider } from 'app/hooks/access-sharing';
import { DeleteDialog } from 'app/components/dialogs';
import { removeEmojis } from 'utils/general';
import { filterSharedFleets } from 'utils/searchFilter';
import {
  SharedItem,
  SharedItemShareType,
  sharesApi,
} from 'services/api/shares';
import { useNotificationsContext } from 'context/useNotificationsContext';
import { ErrorNotification, Success } from 'app/components/Notifications';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import assert from 'assert';
import { useGetUserInfoQuery } from 'app/hooks/user-info';
import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';
import { FilterTabs } from 'app/components/FilterTabs';
import { invitationsApi } from 'services/api/invitations/queries';

interface ISharedFleets {
  isMobile: boolean;
  sharedFleets: SharedItem[];
}

function SharedFleets(props: ISharedFleets) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deleteFleet] = sharesApi.useDeleteFleetMutation();
  const [updateFleet] = sharesApi.useUpdateFleetMutation();
  const { addNotification } = useNotificationsContext();
  const { addErrorModal } = React.useContext(GlobalErrorContext);
  const { optimisticallyRemoveEquipments } = useContext(EquipmentListContext);
  const [revokeInvitation] = invitationsApi.useRevokeInvitationMutation();

  const { data: user } = useGetUserInfoQuery();

  const [searchValue, setSearchValue] = React.useState<string>('');

  const [isExpanded, setIsExpanded] = React.useState(false);

  const [selectedFleet, setSelectedFleet] = useState<SharedItem>();

  const [isFleetNameUpdating, setIsFleetNameUpdating] = useState(false);
  const [isEditFleetNameDialogOpen, setIsEditFleetNameDialogOpen] =
    useState(false);

  const [isFleetUpdating, setIsFleetUpdating] = useState(false);
  const [isEditFleetDialogOpen, setIsEditFleetDialogOpen] = useState(false);

  const [isDeleteFleetDialogOpen, setIsDeleteFleetDialogOpen] = useState(false);
  const [isRevokeOwnAccessDialogOpen, setIsRevokeOwnAccessDialogOpen] =
    useState(false);

  const [filter, setFilter] = React.useState(AcessSharingFilters.All);

  const filterOptions = [
    { value: AcessSharingFilters.All, label: t('all') },
    { value: AcessSharingFilters.OwnedByMe, label: t('owned_by_me') },
    { value: AcessSharingFilters.SharedWithMe, label: t('shared_with_me') },
  ];

  /* TODO: Remove this once we have a real data */
  const sortedFleets = React.useMemo(() => {
    const sharedFleets = [...(props.sharedFleets ?? [])];

    // Apply search filter using utility function
    const filteredFleets = filterSharedFleets(sharedFleets, searchValue);

    return filteredFleets.sort((a, b) => {
      return a.sharedItemDetails.sharedItemLabel.localeCompare(
        b.sharedItemDetails.sharedItemLabel,
      );
    });
  }, [props.sharedFleets, searchValue]);

  const handleChangeFleetName = useTryMutate(
    (name: string) => {
      assert(selectedFleet);
      setIsFleetNameUpdating(true);
      return updateFleet({
        exposedFleetId: selectedFleet.sharedItemDetails.exposedId,
        name,
      });
    },
    {
      onComplete: (res, err, arg, api) => {
        setIsFleetNameUpdating(false);
        if (err) {
          api.showErrorModal();
        } else {
          api.successNotification(
            t('access_sharing.notifications.fleet_name_successfully_updated'),
          );
          setIsEditFleetNameDialogOpen(false);
        }
      },
    },
  );

  const handleDeleteFleet = async () => {
    try {
      assert(selectedFleet);
      await deleteFleet({
        exposedFleetId: selectedFleet.sharedItemDetails.exposedId,
      }).unwrap();

      addNotification(
        <Success
          text={t('access_sharing.notifications.fleet_successfully_deleted')}
        />,
      );
      setIsDeleteFleetDialogOpen(false);
    } catch (error) {
      console.error(error);
      addErrorModal();
    }
  };

  const handleRevokeAccess = async () => {
    const exposedAccessSharingRecordId = selectedFleet?.sharedWith.find(
      sharedWith => sharedWith.email === user?.email,
    )?.exposedAccessSharingRecordId;

    if (!exposedAccessSharingRecordId) return;

    try {
      await revokeInvitation([
        {
          shareType: SharedItemShareType.FLEET,
          exposedAccessSharingRecordId,
        },
      ]);

      const equipmentIds = selectedFleet.sharedItemDetails?.exposedEquipmentIds;
      if (!!equipmentIds?.length) {
        optimisticallyRemoveEquipments(equipmentIds);
      }

      addNotification(
        <Success text={t('access_sharing.snackbars.access-revoked')} />,
      );
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={'Server error'} />);
    } finally {
      setIsRevokeOwnAccessDialogOpen(false);
    }
  };
  const handleChangeFleet = useTryMutate(
    ({
      name,
      exposedEquipmentIds,
    }: {
      name: string;
      exposedEquipmentIds: string[];
    }) => {
      assert(selectedFleet);
      setIsFleetUpdating(true);
      return updateFleet({
        exposedFleetId: selectedFleet.sharedItemDetails.exposedId,
        name,
        exposedEquipmentIds,
      });
    },
    {
      onComplete: (res, err, arg, api) => {
        setIsFleetUpdating(false);
        if (err) {
          api.showErrorModal();
        } else {
          api.successNotification(
            t('access_sharing.notifications.fleet_successfully_updated'),
          );
          setIsEditFleetDialogOpen(false);
        }
      },
    },
  );

  return (
    <>
      <div className="access-sharing-subcontainer">
        <div className="access-sharing-subcontainer-header">
          {props.sharedFleets?.length == 0 ? (
            <h3>{t('shared_fleets_title')}</h3>
          ) : (
            <div className="access-sharing-subcontainer-header-content">
              <h3>{t('shared_fleets_title')}</h3>
              <div className="site-search open">
                <span className="search-icon">
                  <img alt="Search" src={SearchIcon}></img>
                </span>
                <div className="search-input">
                  <Input
                    name="equipment_fleets_search"
                    value={searchValue}
                    placeholder={t('shared_equipment_search_placeholder')}
                    onChange={newValue => {
                      setSearchValue(removeEmojis(newValue.value));
                    }}
                    onClick={() => setIsExpanded(!isExpanded)}
                  />
                  {searchValue && (
                    <span
                      className="search-icon-close"
                      onClick={() => {
                        setSearchValue('');
                      }}
                    >
                      <BlackCloseIcon />
                    </span>
                  )}
                </div>
              </div>

              <FilterTabs
                options={filterOptions}
                activeFilter={filter}
                onChange={setFilter}
              />
            </div>
          )}
        </div>
        <div className="access-sharing-subcontainer-body">
          {sortedFleets?.length == 0 ? (
            <div className="access-sharing-subcontainer-body-empty">
              <div className="access-sharing-body-picture">
                <img src={NoSharedFleets}></img>
              </div>
              <div className="access-sharing-body-text">
                <h4>{t('shared_fleets_no_fleets_title')}</h4>
                <p>{t('shared_fleets_no_fleets_text')}</p>
              </div>
            </div>
          ) : (
            <div className="access-sharing-subcontainer-body-table">
              <div className="access-sharing-subcontainer-body-table-header">
                <div>{t('fleet')}</div>
                <div>{t('my_access')}</div>
                <div>{t('owner')}</div>
                <div></div>
                <div>{t('shared_with')}</div>
                <div></div>
              </div>
              <div className="access-sharing-subcontainer-body-table-body">
                {sortedFleets.map(fleet => (
                  <SharedFleetsRow
                    key={
                      fleet.sharedItemDetails.exposedId +
                      fleet.sharedItemDetails.sharedItemLabel
                    }
                    fleet={fleet}
                    isMobile={props.isMobile}
                    onEditFleet={() => {
                      setSelectedFleet(fleet);
                      setIsEditFleetDialogOpen(true);
                    }}
                    onEditFleetName={() => {
                      setSelectedFleet(fleet);
                      setIsEditFleetNameDialogOpen(true);
                    }}
                    onDeleteFleet={() => {
                      setSelectedFleet(fleet);

                      setIsDeleteFleetDialogOpen(true);
                    }}
                    onRevokeOwnAccess={() => {
                      setSelectedFleet(fleet);
                      setIsRevokeOwnAccessDialogOpen(true);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          <div className="access-sharing-subcontainer-body-buttons-container">
            <Button
              className="content-button round-button"
              themeColor="tertiary"
              onClick={() => navigate('/access/create-fleet')}
            >
              {t('shared_fleets_create_button')}
            </Button>
            <Button
              className="content-button round-button"
              themeColor="secondary"
              onClick={() => navigate('/access/share-fleet')}
            >
              {t('shared_fleets_share_button')}
            </Button>
          </div>
        </div>
      </div>

      {isEditFleetNameDialogOpen && (
        <EditFleetNameDialog
          initialValue={selectedFleet?.sharedItemDetails.sharedItemLabel!}
          isLoading={isFleetNameUpdating}
          onChange={handleChangeFleetName}
          onClose={() => setIsEditFleetNameDialogOpen(false)}
        />
      )}

      <ShareAccessEquipmentGuideProvider>
        {isEditFleetDialogOpen && (
          <EditFleetDialog
            initialName={selectedFleet?.sharedItemDetails.sharedItemLabel!}
            exposedEquipmentIds={
              selectedFleet?.sharedItemDetails.exposedEquipmentIds
            }
            isLoading={isFleetUpdating}
            onChange={handleChangeFleet}
            onClose={() => setIsEditFleetDialogOpen(false)}
          />
        )}
      </ShareAccessEquipmentGuideProvider>

      {isDeleteFleetDialogOpen && (
        <DeleteDialog
          open
          title={t('access_sharing.dialogs.delete_fleet.title', {
            name: selectedFleet?.sharedItemDetails.sharedItemLabel,
          })}
          confirmationLabel={t(
            'access_sharing.dialogs.delete_fleet.actions.delete',
          )}
          onClose={() => setIsDeleteFleetDialogOpen(false)}
          onConfirm={handleDeleteFleet}
        />
      )}

      {isRevokeOwnAccessDialogOpen && (
        <DeleteDialog
          open
          title={t('revoke_access_confirmation', {
            email: user?.email,
            machineName: selectedFleet?.sharedItemDetails.sharedItemLabel,
          })}
          confirmationLabel={t('access_sharing.role_based_actions.revoke')}
          onClose={() => setIsRevokeOwnAccessDialogOpen(false)}
          onConfirm={handleRevokeAccess}
        />
      )}
    </>
  );
}

export default SharedFleets;
